import { Step, StepLabel, Stepper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Check } from "@mui/icons-material";
import { StepIconProps } from "@mui/material/StepIcon";
import { CustomizedStepperProps } from "../Utils/Types";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { isMobile } from "../Utils/Utils";

/**
 * A styled version of the {@link StepConnector} component.
 * @see https://mui.com/material-ui/api/step-connector/
 */
const QontoConnector = styled(StepConnector)(({ theme }) => ({
  /**
   * Styles for the connector line that is shown when the step has an alternative label.
   */
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },

  /**
   * Styles for the connector line when the step is active.
   */
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.successColor?.main,
    },
  },

  /**
   * Styles for the connector line when the step is completed.
   */
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.successColor?.main,
    },
  },

  /**
   * Styles for the connector line.
   */
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "#eaeaf0",
    borderTopWidth: 3,
    borderRadius: 1,
    ...theme.applyStyles("dark", {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

/**
 * A styled component that renders a div that will be used as a step icon.
 *
 * @param {StepIconProps} props - The props passed to the component.
 * @returns {ReactElement} A styled div that will be used as a step icon.
 */
const QontoStepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(
  ({ theme }) => ({
    // The color of the icon.
    color: "#eaeaf0",
    display: "flex",
    height: 22,
    alignItems: "center",
    "& .QontoStepIcon-completedIcon": {
      color: theme.palette.successColor?.main,
      zIndex: 1,
      fontSize: 18,
    },
    "& .QontoStepIcon-circle": {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[700],
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          color: theme.palette.successColor?.main,
        },
      },
    ],
  })
);

/**
 * A component that renders a styled div that will be used as a step icon.
 *
 * @param {StepIconProps} props - The props passed to the component.
 * @returns {ReactElement} A styled div that will be used as a step icon.
 *
 * @example
 * <QontoStepIcon active={true} completed={true} />
 */
function QontoStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

/**
 * CustomizedStepper renders a Material-UI Stepper component using custom step icons and connectors.
 *
 * @param {CustomizedStepperProps} props - The properties for the stepper, including:
 *    - selectedStep: The currently active step.
 *    - stepsArray: An array of step objects, each containing an ID and a name.
 *
 * The component determines the active step based on the `selectedStep`'s ID and highlights it.
 * It uses `QontoConnector` for custom step connector styles and `QontoStepIcon` for custom step icons.
 */

export default function CustomizedStepper(props: CustomizedStepperProps) {
  const { selectedStep, stepsArray, onStepChange } = props;
  const activeStep = stepsArray?.findIndex((st) => st._id === selectedStep?._id);
  return (
    <Stepper
      alternativeLabel
      activeStep={activeStep}
      connector={<QontoConnector />}
      sx={{ width: isMobile() ? "95vw" : "80vw", marginBottom: "10px" }}
    >
      {stepsArray?.map((stEnum, index: number) => (
        <Step
          key={stEnum.id}
          completed={index <= activeStep}
          onClick={() => onStepChange(stEnum)}
        >
          <StepLabel key={stEnum.id} StepIconComponent={QontoStepIcon}>{stEnum.name}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}
